/**
 * Session-scoped cache for the /api/legislators listing.
 *
 * Why sessionStorage (not localStorage or React state)?
 * - localStorage persists across tabs/sessions → we'd serve stale rosters after
 *   a Congress turnover. sessionStorage clears when the tab closes, so stale
 *   data never outlives a single browsing session.
 * - React state alone only caches within a single page; each route-change to
 *   /compare or /legislators refetches the 536-row payload even though the
 *   roster hasn't changed.
 *
 * Callers should hydrate synchronously on mount (before first paint) and then
 * refresh silently in the background. Cached payload is keyed by Congress
 * number because the listing endpoint takes `?congress=N`.
 */

const PREFIX = "whip:legislators:v1";

export const legislatorCacheKey = (congress) =>
  congress == null ? PREFIX : `${PREFIX}:${congress}`;

export const readLegislatorCache = (congress) => {
  try {
    const raw = sessionStorage.getItem(legislatorCacheKey(congress));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch {
    return null;
  }
};

export const writeLegislatorCache = (congress, data) => {
  try {
    if (Array.isArray(data) && data.length > 0) {
      sessionStorage.setItem(legislatorCacheKey(congress), JSON.stringify(data));
    }
  } catch {
    /* quota exceeded or private mode, fail silently */
  }
};
